// src/pages/ConfigPage.js
import React, { useEffect, useState, useCallback } from "react";
import { loadConfig, saveConfig, DEFAULT_CONFIG } from "../utils/config";
import { formatDate } from "../utils/dates";

// ── Shared section wrapper ────────────────────────────────────────────────────
function SectionHeader({ title, description }) {
  return (
    <div style={{ marginBottom: 18, paddingBottom: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--purple-dark)" }}>{title}</div>
      {description && <div style={{ fontSize: 13, color: "var(--gray-500)", marginTop: 3 }}>{description}</div>}
    </div>
  );
}

// ── Tag editor ────────────────────────────────────────────────────────────────
function TagEditor({ label, hint, items, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const val = input.trim();
    if (!val || items.includes(val)) return;
    onChange([...items, val]);
    setInput("");
  };
  const remove  = (item) => onChange(items.filter(i => i !== item));
  const moveUp   = (idx) => { if (idx === 0) return; const n = [...items]; [n[idx-1],n[idx]]=[n[idx],n[idx-1]]; onChange(n); };
  const moveDown = (idx) => { if (idx===items.length-1) return; const n=[...items]; [n[idx],n[idx+1]]=[n[idx+1],n[idx]]; onChange(n); };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{label}</h3>
      {hint && <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 12 }}>{hint}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
        {items.map((item, idx) => (
          <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--gray-100)", borderRadius: "var(--radius)", padding: "8px 12px" }}>
            <span style={{ flex: 1, fontSize: 14 }}>{item}</span>
            <button className="btn-ghost" style={{ padding: "2px 6px", fontSize: 12 }} onClick={() => moveUp(idx)} disabled={idx===0}>↑</button>
            <button className="btn-ghost" style={{ padding: "2px 6px", fontSize: 12 }} onClick={() => moveDown(idx)} disabled={idx===items.length-1}>↓</button>
            <button className="btn-ghost" style={{ padding: "2px 6px", fontSize: 12, color: "var(--red)" }} onClick={() => remove(item)}>✕</button>
          </div>
        ))}
        {items.length === 0 && <div style={{ fontSize: 13, color: "var(--gray-400)", padding: "8px 0" }}>No options defined yet.</div>}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Type a new option and press Add or Enter…" style={{ flex: 1 }} />
        <button className="btn-primary" style={{ padding: "7px 16px" }} onClick={add}>Add</button>
      </div>
    </div>
  );
}

// ── Interval editor ───────────────────────────────────────────────────────────
function IntervalEditor({ items, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const val = parseInt(input, 10);
    if (!val || val < 1 || val > 120 || items.includes(val)) return;
    onChange([...items, val].sort((a, b) => a - b));
    setInput("");
  };
  const remove = (item) => onChange(items.filter(i => i !== item));

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Audit Intervals</h3>
      <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 12 }}>Available audit frequency options in months. Sorted automatically.</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        {items.map(item => (
          <div key={item} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--purple-light)", borderRadius: 99, padding: "4px 12px" }}>
            <span style={{ fontSize: 14, color: "var(--purple-dark)", fontWeight: 500 }}>{item} {item === 1 ? "month" : "months"}</span>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: "var(--purple-dark)", fontSize: 13, padding: 0 }} onClick={() => remove(item)}>✕</button>
          </div>
        ))}
        {items.length === 0 && <span style={{ fontSize: 13, color: "var(--gray-400)" }}>No intervals defined.</span>}
      </div>
      <div style={{ display: "flex", gap: 8, maxWidth: 320 }}>
        <input type="number" min="1" max="120" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Enter months (e.g. 3)" style={{ flex: 1 }} />
        <button className="btn-primary" style={{ padding: "7px 16px" }} onClick={add}>Add</button>
      </div>
    </div>
  );
}

// ── Custom user manager ───────────────────────────────────────────────────────
function CustomUserManager() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [form,    setForm]    = useState({ displayName: "", jobTitle: "", email: "" });

  const load = async () => {
    setLoading(true);
    try { const res = await fetch("/api/custom-users"); const data = await res.json(); setUsers(Array.isArray(data) ? data : []); }
    catch { setUsers([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.displayName.trim() || !form.email.trim()) { setError("Name and email are required"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch("/api/custom-users", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setForm({ displayName: "", jobTitle: "", email: "" });
      await load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from custom users?`)) return;
    try { await fetch(`/api/custom-users/${id}`, { method: "DELETE" }); await load(); }
    catch (err) { setError(err.message); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Custom Users</h3>
      <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 14 }}>
        Add users not in Azure AD. Custom users can be assigned to systems but cannot be system owners. Department is automatically set to <strong>External</strong>.
      </p>
      {error && <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "8px 12px", borderRadius: "var(--radius)", marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading ? <div style={{ textAlign: "center", padding: 20 }}><span className="spinner" /></div> : users.length > 0 && (
        <div style={{ marginBottom: 14, background: "var(--gray-50)", borderRadius: "var(--radius)", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["Name", "Job Title", "Email", "Dept", ""].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 500 }}>{u.displayName}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "var(--gray-500)" }}>{u.jobTitle || "—"}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "var(--gray-500)" }}>{u.email}</td>
                  <td style={{ padding: "9px 12px" }}><span className="badge badge-neutral" style={{ fontSize: 11 }}>External</span></td>
                  <td style={{ padding: "9px 12px" }}>
                    <button className="btn-ghost" style={{ color: "var(--red)", fontSize: 13 }} onClick={() => handleDelete(u.id, u.displayName)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ background: "var(--gray-50)", borderRadius: "var(--radius)", padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, color: "var(--gray-700)" }}>Add Custom User</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "flex-end" }}>
          <div><label style={{ fontSize: 12 }}>Name *</label><input value={form.displayName} onChange={e => set("displayName", e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder="Full name" style={{ marginTop: 4 }} /></div>
          <div><label style={{ fontSize: 12 }}>Job Title</label><input value={form.jobTitle} onChange={e => set("jobTitle", e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder="e.g. Contractor" style={{ marginTop: 4 }} /></div>
          <div><label style={{ fontSize: 12 }}>Email *</label><input value={form.email} onChange={e => set("email", e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder="email@example.com" type="email" style={{ marginTop: 4 }} /></div>
          <button className="btn-primary" onClick={handleAdd} disabled={saving} style={{ padding: "7px 16px", whiteSpace: "nowrap" }}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Add User"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm restore dialog ────────────────────────────────────────────────────
function ConfirmRestoreDialog({ blob, onConfirm, onClose }) {
  const [text,     setText]     = useState("");
  const [fullWipe, setFullWipe] = useState(false);
  const [working,  setWorking]  = useState(false);
  const [error,    setError]    = useState("");
  const isOperational = blob.name.startsWith("operational/");
  const date = formatDate(blob.lastModified);
  const filename = blob.name.split("/").pop().replace(".json", "");

  const handleConfirm = async () => {
    if (text !== "CONFIRM") { setError('Please type CONFIRM exactly to proceed.'); return; }
    setWorking(true); setError("");
    try {
      const res  = await fetch("/api/backups/restore", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blobName: blob.name, fullWipe: fullWipe && isOperational, confirm: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Restore failed");
      onConfirm(data);
    } catch (err) { setError(err.message); setWorking(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Restore from Backup</h2>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: "var(--orange-light)", borderRadius: "var(--radius)", padding: "12px 14px", marginBottom: 16, fontSize: 13 }}>
            <strong>⚠ You are about to restore:</strong><br />
            <span style={{ fontWeight: 500 }}>{isOperational ? "Operational Data" : "Configuration"}</span> backup from <strong>{date}</strong> ({filename})
          </div>

          {isOperational && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={fullWipe} onChange={e => setFullWipe(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  <strong>Full wipe before restore</strong> — delete all current systems and replace with backup contents.
                  <span style={{ display: "block", color: "var(--gray-500)", marginTop: 2 }}>Leave unticked to upsert (add/update without deleting existing records).</span>
                  {fullWipe && <span style={{ display: "block", color: "var(--red)", fontWeight: 500, marginTop: 4 }}>⚠ All current systems will be permanently deleted.</span>}
                </span>
              </label>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>
              Type <code style={{ background: "var(--gray-100)", padding: "1px 6px", borderRadius: 4 }}>CONFIRM</code> to proceed:
            </label>
            <input value={text} onChange={e => { setText(e.target.value); setError(""); }}
              placeholder="CONFIRM" style={{ fontWeight: 600, letterSpacing: 2 }} />
          </div>

          {error && <div style={{ background: "var(--red-light)", color: "var(--red)", borderRadius: "var(--radius)", padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>{error}</div>}

          <div className="form-actions">
            <button className="btn-secondary" onClick={onClose} disabled={working}>Cancel</button>
            <button className="btn-primary" onClick={handleConfirm} disabled={working || text !== "CONFIRM"}
              style={{ background: "var(--red)", opacity: text === "CONFIRM" ? 1 : 0.5 }}>
              {working ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Restoring…</> : "Restore"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Backup & Restore ──────────────────────────────────────────────────────────
function BackupRestore() {
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError,  setImportError]  = useState("");
  const [fullWipe,     setFullWipe]     = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType,     setFileType]     = useState(null);
  const [blobs,        setBlobs]        = useState({ operational: [], configuration: [] });
  const [blobsLoading, setBlobsLoading] = useState(true);
  const [blobsError,   setBlobsError]   = useState("");
  const [confirmBlob,  setConfirmBlob]  = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [confirmImportText, setConfirmImportText] = useState("");

  const loadBlobs = useCallback(async () => {
    setBlobsLoading(true); setBlobsError("");
    try {
      const res  = await fetch("/api/backups");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setBlobs(data);
    } catch (err) { setBlobsError(err.message); }
    finally { setBlobsLoading(false); }
  }, []);

  useEffect(() => { loadBlobs(); }, [loadBlobs]);

  const handleExport = async (type) => {
    try {
      const res = await fetch(`/api/export/${type}`);
      if (!res.ok) throw new Error(`Export failed`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `sgp-${type}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { alert("Export failed: " + err.message); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file); setImportResult(null); setImportError(""); setFileType(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try { const data = JSON.parse(ev.target.result); setFileType(data.exportType || "unknown"); }
      catch { setImportError("Invalid file — could not parse JSON."); }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    if (!confirmImport) { setConfirmImport(true); return; }
    if (confirmImportText !== "CONFIRM") { setImportError("Please type CONFIRM exactly to proceed."); return; }
    setImporting(true); setImportError(""); setImportResult(null); setConfirmImport(false); setConfirmImportText("");
    try {
      const text = await selectedFile.text();
      const res  = await fetch(`/api/import${fullWipe ? "?fullWipe=true" : ""}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: text,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Import failed");
      setImportResult(result);
      setSelectedFile(null); setFileType(null);
      document.getElementById("restore-file-input").value = "";
    } catch (err) { setImportError(err.message); }
    finally { setImporting(false); }
  };

  const handleBlobRestore = (result) => {
    setConfirmBlob(null);
    setImportResult(result);
    loadBlobs();
  };

  const formatBytes = (b) => b < 1024 ? `${b} B` : `${(b/1024).toFixed(1)} KB`;

  const BlobTable = ({ items, type }) => (
    items.length === 0
      ? <div style={{ fontSize: 13, color: "var(--gray-400)", padding: "8px 0" }}>No {type} backups found yet.</div>
      : <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
          <thead>
            <tr style={{ background: "var(--gray-50)" }}>
              {["Date", "File", "Size", ""].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map(blob => (
              <tr key={blob.name}>
                <td style={{ padding: "8px 10px", fontSize: 13 }}>{formatDate(blob.lastModified)}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "var(--gray-500)", fontFamily: "monospace" }}>{blob.name.split("/").pop()}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "var(--gray-500)" }}>{formatBytes(blob.size)}</td>
                <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                  <button className="btn-ghost" style={{ fontSize: 12, marginRight: 4 }}
                    onClick={async () => {
                      const res = await fetch(`/api/backups/download?blob=${encodeURIComponent(blob.name)}`);
                      const b   = await res.blob();
                      const url = URL.createObjectURL(b);
                      const a   = document.createElement("a");
                      a.href = url; a.download = blob.name.split("/").pop();
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    }}>⬇ Download</button>
                  <button className="btn-danger" style={{ fontSize: 12 }}
                    onClick={() => setConfirmBlob(blob)}>↩ Restore</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
  );

  const sectionStyle = { background: "var(--gray-50)", borderRadius: "var(--radius)", padding: "16px 18px", marginBottom: 12 };

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Automated Weekly Backups</h3>
        <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 14 }}>
          Your Logic App scheduler is configured to call <code style={{ fontSize: 12 }}>POST /api/scheduled-backup</code> weekly,
          saving two JSON files to a private <code style={{ fontSize: 12 }}>sgp-backups/</code> container in your Azure Storage account —
          one for operational data (systems, users, audit logs) and one for configuration (dropdowns, email templates).
          The 10 most recent backups of each type are shown below. To restore, click <strong>Restore</strong> next to any backup
          and type <strong>CONFIRM</strong> when prompted.
        </p>

        {blobsLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}><span className="spinner" /></div>
        ) : blobsError ? (
          <div style={{ background: "var(--orange-light)", borderRadius: "var(--radius)", padding: "10px 14px", fontSize: 13 }}>
            <strong>Could not load backups:</strong> {blobsError}
            <br /><span style={{ color: "var(--gray-500)" }}>This is normal if the scheduled backup hasn't run yet. Backups are saved to the <code>sgp-backups</code> container in your storage account.</span>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Operational Data</div>
              <BlobTable items={blobs.operational} type="operational" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Configuration</div>
              <BlobTable items={blobs.configuration} type="configuration" />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button className="btn-ghost" style={{ fontSize: 12 }} onClick={loadBlobs}>⟳ Refresh</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Manual Export</h3>
        <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 12 }}>
          Download a backup file immediately. The file type is detected automatically on upload — either can be restored below.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={sectionStyle}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Operational Data</div>
            <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 10 }}>Systems, users, audit logs, off-boarding, custom users</div>
            <button className="btn-secondary" style={{ fontSize: 13, width: "100%" }} onClick={() => handleExport("operational")}>⬇ Export Operational</button>
          </div>
          <div style={sectionStyle}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Configuration</div>
            <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 10 }}>Dropdown options, email templates</div>
            <button className="btn-secondary" style={{ fontSize: 13, width: "100%" }} onClick={() => handleExport("configuration")}>⬇ Export Configuration</button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Restore from File</h3>
        <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 12 }}>
          Upload either an operational or configuration backup file — the type is detected automatically and restored to the correct tables.
        </p>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>Select backup file (.json)</label>
          <input id="restore-file-input" type="file" accept=".json" onChange={handleFileChange} style={{ fontSize: 13 }} />
          {fileType && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--purple)", fontWeight: 500 }}>
              ✓ Detected: <strong>{fileType === "operational" ? "Operational Data" : fileType === "configuration" ? "Configuration" : "Unknown"}</strong> backup
            </div>
          )}
        </div>

        {fileType === "operational" && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={fullWipe} onChange={e => setFullWipe(e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 13 }}>
                <strong>Full wipe before restore</strong> — deletes all current systems then imports backup
                {fullWipe && <span style={{ display: "block", color: "var(--red)", fontWeight: 500, marginTop: 2 }}>⚠ All current systems will be permanently deleted.</span>}
                {!fullWipe && <span style={{ display: "block", color: "var(--gray-500)", marginTop: 2 }}>Default: upsert mode — adds/updates records without deleting anything.</span>}
              </span>
            </label>
          </div>
        )}

        {importError && <div style={{ background: "var(--red-light)", color: "var(--red)", borderRadius: "var(--radius)", padding: "8px 12px", marginBottom: 10, fontSize: 13 }}>{importError}</div>}
        {importResult && (
          <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "10px 14px", marginBottom: 10, fontSize: 13 }}>
            <strong>✓ Restore complete</strong> — {importResult.imported} records restored from {importResult.exportType} backup.
            {importResult.errors?.length > 0 && <div style={{ marginTop: 4, color: "var(--orange)" }}>{importResult.errors.length} error(s): {importResult.errors.map(e => e.item).join(", ")}</div>}
          </div>
        )}

        <button className="btn-primary" onClick={handleImport} disabled={!selectedFile || !fileType || fileType === "unknown" || importing} style={{ fontSize: 13 }}>
          {importing ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Restoring…</> : "↩ Restore from File"}
        </button>
      </div>

      {confirmBlob && (
        <ConfirmRestoreDialog blob={confirmBlob} onConfirm={handleBlobRestore} onClose={() => setConfirmBlob(null)} />
      )}
    </>
  );
}


// ── Main page ─────────────────────────────────────────────────────────────────

// ── Merge Custom User to Entra ────────────────────────────────────────────────
function MergeCustomUsersSection() {
  const [customUsers, setCustomUsers] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [merging,     setMerging]     = useState(null);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  // Per-custom-user state: { [cuId]: { query, results, selected, confirm } }
  const [state, setState] = useState({});
  const timers = React.useRef({});

  useEffect(() => {
    fetch("/api/custom-users").then(r=>r.json()).then(setCustomUsers).catch(()=>[]).finally(()=>setLoading(false));
  }, []);

  const setS = (cuId, patch) => setState(p => ({ ...p, [cuId]: { ...p[cuId], ...patch } }));

  const searchEntra = (cuId, q) => {
    setS(cuId, { query: q, selected: null });
    clearTimeout(timers.current[cuId]);
    if (q.length < 2) { setS(cuId, { results: [] }); return; }
    timers.current[cuId] = setTimeout(async () => {
      try {
        // ownerOnly=true ensures only Entra users, no custom users
        const res  = await fetch(`/api/graph/users?q=${encodeURIComponent(q)}&ownerOnly=true`);
        const data = await res.json();
        setS(cuId, { results: Array.isArray(data) ? data.slice(0, 6) : [] });
      } catch {}
    }, 300);
  };

  const doMerge = async (cu) => {
    const s = state[cu.id] || {};
    if (!s.selected) return;
    setMerging(cu.id); setError(""); setSuccess("");
    try {
      const res = await fetch(`/api/custom-users/${cu.id}/merge`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entraUpn: s.selected.upn, entraDisplayName: s.selected.displayName, entraEmail: s.selected.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCustomUsers(prev => prev.filter(u => u.id !== cu.id));
      setSuccess(`${cu.displayName} merged to ${s.selected.displayName} — ${data.systemsUpdated} system(s) updated.`);
    } catch (err) { setError(err.message); }
    finally { setMerging(null); }
  };

  if (loading) return <div style={{textAlign:"center",padding:20}}><span className="spinner"/></div>;
  if (customUsers.length === 0) return <div style={{fontSize:13,color:"var(--gray-400)"}}>No custom users to merge.</div>;

  return (
    <div>
      {error   && <div style={{background:"var(--red-light)",color:"var(--red)",borderRadius:"var(--radius)",padding:"8px 12px",fontSize:13,marginBottom:10}}>{error}</div>}
      {success && <div style={{background:"#D1FAE5",color:"#065F46",borderRadius:"var(--radius)",padding:"8px 12px",fontSize:13,marginBottom:10}}>✓ {success}</div>}
      <p style={{fontSize:13,color:"var(--gray-600)",marginBottom:12}}>When a custom user now has an Entra account, search for them below. Select the matching Entra user and type CONFIRM to merge — their system assignments will transfer to the Entra UPN.</p>
      {customUsers.map(cu => {
        const s = state[cu.id] || {};
        return (
          <div key={cu.id} style={{borderRadius:"var(--radius)",padding:"12px 14px",marginBottom:8,background:"var(--gray-50)"}}>
            <div style={{fontWeight:600,fontSize:13,marginBottom:2}}>{cu.displayName}</div>
            <div style={{fontSize:11,color:"var(--gray-400)",marginBottom:10}}>{cu.email}{cu.jobTitle ? ` · ${cu.jobTitle}` : ""}</div>

            {/* Step 1 — search */}
            {!s.selected && (
              <div style={{position:"relative"}}>
                <input value={s.query||""} onChange={e=>searchEntra(cu.id, e.target.value)}
                  placeholder="Search Entra for matching user…" style={{fontSize:12,width:"100%"}} />
                {(s.results||[]).length > 0 && (
                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",borderRadius:"var(--radius)",boxShadow:"var(--shadow)",zIndex:100,maxHeight:200,overflowY:"auto"}}>
                    {s.results.map(u => (
                      <div key={u.upn} style={{padding:"8px 10px",cursor:"pointer",fontSize:12,}}
                        onMouseEnter={e=>e.currentTarget.style.background="var(--gray-50)"}
                        onMouseLeave={e=>e.currentTarget.style.background=""}
                        onClick={()=>setS(cu.id, { selected:u, results:[], query:u.displayName })}>
                        <div style={{fontWeight:500}}>{u.displayName}</div>
                        <div style={{color:"var(--gray-400)",fontSize:11}}>{u.upn}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2 — confirm */}
            {s.selected && (
              <div style={{background:"var(--purple-light)",borderRadius:"var(--radius)",padding:"10px 12px"}}>
                <div style={{fontSize:13,marginBottom:8}}>
                  Merge <strong>{cu.displayName}</strong> → <strong>{s.selected.displayName}</strong> ({s.selected.upn})?
                  <br/><span style={{fontSize:11,color:"var(--gray-600)"}}>All system assignments will transfer to the Entra UPN. This cannot be undone.</span>
                </div>
                <div style={{fontSize:12,marginBottom:6}}>Type <code style={{background:"rgba(0,0,0,0.08)",padding:"1px 6px",borderRadius:4}}>CONFIRM</code> to proceed:</div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input value={s.confirmText||""} onChange={e=>setS(cu.id,{confirmText:e.target.value})}
                    placeholder="CONFIRM" style={{fontSize:12,fontWeight:600,letterSpacing:2,width:130}} />
                  <button className="btn-primary" style={{fontSize:12}} disabled={merging===cu.id||s.confirmText!=="CONFIRM"} onClick={()=>doMerge(cu)}>
                    {merging===cu.id ? <><span className="spinner" style={{width:12,height:12}}/> Merging…</> : "Confirm Merge"}
                  </button>
                  <button className="btn-ghost" style={{fontSize:12}} onClick={()=>setS(cu.id,{selected:null,confirmText:"",query:""})}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


export default function ConfigPage() {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");
  const [tab,     setTab]     = useState("users");

  useEffect(() => { loadConfig().then(c => { setConfig(c); setLoading(false); }); }, []);

  const update = (key, val) => { setConfig(c => ({ ...c, [key]: val })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true); setError("");
    try { await saveConfig(config); setSaved(true); setTimeout(() => setSaved(false), 3000); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    if (!window.confirm("Reset all configuration to defaults? This cannot be undone.")) return;
    setConfig({ ...DEFAULT_CONFIG }); setSaved(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><span className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="page-header">
        <div>
          <div className="page-title">Configuration</div>
          <div className="page-subtitle">Manage dropdown options, users and backup settings</div>
        </div>
        {tab === "fields" && (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {saved  && <span style={{ fontSize: 13, color: "var(--green)", fontWeight: 500 }}>✓ Saved</span>}
            {error  && <span style={{ fontSize: 13, color: "var(--red)" }}>{error}</span>}
            <button className="btn-secondary" onClick={handleReset}>Reset to defaults</button>
            <button className="btn-primary"   onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ display:"flex", marginBottom:24 }}>
        {[
          ["users",     "User Configuration"],
          ["fields",    "Data Fields"],
          ["backup",    "Backup & Restore"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding:"8px 18px", border:"none", borderRadius:0, background:"transparent",
            cursor:"pointer", fontWeight:500, fontSize:13, whiteSpace:"nowrap",
            color:        tab===key ? "var(--purple)" : "var(--gray-500)",
            borderBottom: tab===key ? "2px solid var(--purple)" : "2px solid transparent",
            marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {/* ── User Configuration ── */}
      {tab === "users" && (
        <>
          <CustomUserManager />
          <div className="card" style={{marginBottom:16}}>
            <h3 style={{fontSize:14,fontWeight:600,marginBottom:4}}>Merge Custom Users to Entra</h3>
            <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:12}}>When a custom user is now in Entra ID, merge their portal assignments to the Entra account and remove the custom user record.</p>
            <MergeCustomUsersSection />
          </div>
        </>
      )}

      {/* ── Data Field Configuration ── */}
      {tab === "fields" && (
        <>
          <TagEditor label="System Categories" hint="Categories used to classify systems on the dashboard."
            items={config.categories} onChange={val => update("categories", val)} />
          <IntervalEditor items={config.auditIntervals} onChange={val => update("auditIntervals", val)} />
          <TagEditor label="Authentication Types" hint="Options available in the Authentication Type field on the system form."
            items={config.authTypes} onChange={val => update("authTypes", val)} />
          <TagEditor label="System Types" hint="Options available in the System Type field on the system form."
            items={config.systemTypes} onChange={val => update("systemTypes", val)} />
          <TagEditor label="Default Access Levels" hint="Used when a system has not defined its own access levels."
            items={config.defaultAccessLevels || []} onChange={val => update("defaultAccessLevels", val)} />
        </>
      )}

      {/* ── Backup & Restore ── */}
      {tab === "backup" && <BackupRestore />}
    </div>
  );
}
